import { useState } from "react";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { classNames, hasOwnProperty } from "maven-lib/dist/utils/misc";
import { CrButton } from "./CrButton";
import { CrCalendar, CrCalendarDateLabel, CrCalendarProps } from "./CrCalendar";
import { CrIconButton } from "./CrIconButton";
import dayjs from "dayjs";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface CrCalendarExtProps extends Omit<CrCalendarProps, "onSelectDate"> {
  allUnknownable: boolean;
  yearUnknownable: boolean;
  monthUnknownable: boolean;
  monthDayUnknownable: boolean;
  dayUnknownable: boolean;
  todaySelectable: boolean;
  typeable: boolean;
  onValueChange: (val: string) => void;
  onDeleteDate: () => void;
  format: "YYYY/MM/DD" | "YYYY-MM-DD" | "DD-MMM-YYYY" | "DD/MMM/YYYY";
  dateLabel?: CrCalendarDateLabel;
  errorMessage?: {
    invalidFormat?: string;
  };
}

export function CrCalendarExt(props: CrCalendarExtProps) {
  const {
    format,
    maxDate,
    minDate,
    onValueChange,
    allUnknownable,
    yearUnknownable,
    monthUnknownable,
    monthDayUnknownable,
    dayUnknownable,
    todaySelectable,
    onDeleteDate,
    dateLabel,
    errorMessage,
    timezone,
  } = props;

  const now = dayjs();

  const [currentDate, setCurrentDate] = useState<Date>(
    ((v) => {
      if (v instanceof Date) return v;
      else if (v && (hasOwnProperty(v, "year") || hasOwnProperty(v, "month") || hasOwnProperty(v, "day")))
        return now
          .set("year", v.year || now.tz(timezone).year())
          .set("month", typeof v.month === "number" ? v.month - 1 : now.tz(timezone).month())
          .set("date", v.day || now.tz(timezone).date())
          .toDate();
      else return new Date();
    })(props.selectedDate)
  );

  const selfTodaySelectable = (() => {
    const maxVal =
      maxDate instanceof Date
        ? dayjs(maxDate.getTime()).format("YYYYMMDD")
        : typeof maxDate === "number"
        ? now.add(maxDate, "days").tz(timezone).format("YYYYMMDD")
        : now.tz(timezone).format("YYYYMMDD");

    const minVal =
      minDate instanceof Date
        ? dayjs(minDate.getTime()).format("YYYYMMDD")
        : typeof minDate === "number"
        ? now.subtract(minDate, "days").tz(timezone).format("YYYYMMDD")
        : now.tz(timezone).format("YYYYMMDD");

    return maxVal >= now.tz(timezone).format("YYYYMMDD") && minVal <= dayjs().tz(timezone).format("YYYYMMDD");
  })();

  const miscCount = [allUnknownable, yearUnknownable, monthUnknownable, monthDayUnknownable, dayUnknownable].filter((e) => e).length;

  const unknowableTypeByFormat = ((format: "YYYY/MM/DD" | "YYYY-MM-DD" | "DD-MMM-YYYY" | "DD/MMM/YYYY") => {
    if (format === "DD-MMM-YYYY") {
      return {
        allUnknownable: "UK-UNK-UKUK",
        yearUnknownable: "DD-MMM-UKUK",
        monthUnknownable: "DD-UNK-YYYY",
        monthDayUnknownable: "UK-UNK-YYYY",
        dayUnknownable: "UK-MMM-YYYY",
      };
    } else if (format === "DD/MMM/YYYY") {
      return {
        allUnknownable: "UK/UNK/UKUK",
        yearUnknownable: "DD/MMM/UKUK",
        monthUnknownable: "DD/UNK/YYYY",
        monthDayUnknownable: "UK/UNK/YYYY",
        dayUnknownable: "UK/MMM/YYYY",
      };
    } else if (format === "YYYY/MM/DD") {
      return {
        allUnknownable: "UKUK/UK/UK",
        yearUnknownable: "UKUK/MM/DD",
        monthUnknownable: "YYYY/UK/DD",
        monthDayUnknownable: "YYYY/UK/UK",
        dayUnknownable: "YYYY/MM/UK",
      };
    } else if (format === "YYYY-MM-DD") {
      return {
        allUnknownable: "UKUK-UK-UK",
        yearUnknownable: "UKUK-MM-DD",
        monthUnknownable: "YYYY-UK-DD",
        monthDayUnknownable: "YYYY-UK-UK",
        dayUnknownable: "YYYY-MM-UK",
      };
    } else {
      return {
        allUnknownable: "UKUK-UK-UK",
        yearUnknownable: "UKUK-MM-DD",
        monthUnknownable: "YYYY-UK-DD",
        monthDayUnknownable: "YYYY-UK-UK",
        dayUnknownable: "YYYY-MM-UK",
      };
    }
  })(format);

  return (
    <div className="cr-calendar-ext">
      <CrCalendar
        onSelectDate={(date) => onValueChange(dayjs(date).tz(timezone).format("YYYYMMDD"))}
        onYearChange={(date) => setCurrentDate(date)}
        onMonthChange={(date) => setCurrentDate(date)}
        timezone={timezone}
        format={format}
        dateLabel={dateLabel}
        errorMessage={{ invalidFormat: errorMessage?.invalidFormat }}
        {...props}
      />
      {(allUnknownable || yearUnknownable || monthUnknownable || monthDayUnknownable || dayUnknownable || todaySelectable) && (
        <div className="cr-calendar-uk">
          {todaySelectable && (
            <CrButton
              className={classNames(miscCount > 1 && "break")}
              disabled={!selfTodaySelectable}
              onClick={() => onValueChange(now.tz(timezone).format("YYYYMMDD"))}
            >
              TODAY
              {miscCount <= 2 && <span className="day">({now.tz(timezone).format(format)})</span>}
            </CrButton>
          )}
          {allUnknownable && (
            <CrButton onClick={() => onValueChange(unknowableTypeByFormat.allUnknownable.replace(/[-//]/g, ""))}>
              {unknowableTypeByFormat.allUnknownable}
            </CrButton>
          )}
          {yearUnknownable && (
            <CrButton
              onClick={() => onValueChange(dayjs(currentDate).format(unknowableTypeByFormat.yearUnknownable.replace(/[-//]/g, "")))}
            >
              {dayjs(currentDate).format(unknowableTypeByFormat.yearUnknownable)}
            </CrButton>
          )}
          {monthUnknownable && (
            <CrButton
              onClick={() => onValueChange(dayjs(currentDate).format(unknowableTypeByFormat.monthUnknownable.replace(/[-//]/g, "")))}
            >
              {dayjs(currentDate).format(unknowableTypeByFormat.monthUnknownable)}
            </CrButton>
          )}
          {monthDayUnknownable && (
            <CrButton
              onClick={() => onValueChange(dayjs(currentDate).format(unknowableTypeByFormat.monthDayUnknownable.replace(/[-//]/g, "")))}
            >
              {dayjs(currentDate).format(unknowableTypeByFormat.monthDayUnknownable)}
            </CrButton>
          )}
          {dayUnknownable && (
            <CrButton onClick={() => onValueChange(dayjs(currentDate).format(unknowableTypeByFormat.dayUnknownable.replace(/[-//]/g, "")))}>
              {dayjs(currentDate).format(unknowableTypeByFormat.dayUnknownable)}
            </CrButton>
          )}
          <CrIconButton color="inner-bordered" onClick={() => onDeleteDate && onDeleteDate()} icon="EraseTool" />
        </div>
      )}
    </div>
  );
}

CrCalendarExt.defaultProps = Object.assign(CrCalendar.defaultProps, {
  yearUnknownable: false,
  monthUnknownable: false,
  monthDayUnknownable: false,
  dayUnknownable: false,
  todaySelectable: true,
});
